import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TranslateModule } from '@ngx-translate/core';

export interface TokenAllocation {
  color: string;
  name: string;
  percentage: number;
}

@Component({
  selector: 'app-allocation-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    FloatLabelModule,
    TranslateModule
  ],
  template: `
    <div class="p-6">
      <form [formGroup]="allocationForm" (ngSubmit)="onSubmit()">
        <div class="flex flex-col gap-6">
          <!-- Color Picker -->
          <div class="flex flex-col gap-2">
            <label for="color" class="text-sm font-medium text-surface-700 dark:text-surface-200">
              {{ 'projectEdit.tokenAllocation.color' | translate }}
            </label>
            <input
              id="color"
              type="color"
              [formControl]="colorControl"
              class="w-full h-12 rounded cursor-pointer border border-surface-300 dark:border-surface-600"
            />
          </div>

          <!-- Name Input -->
          <p-floatlabel>
            <input
              id="name"
              type="text"
              pInputText
              [formControl]="nameControl"
              class="w-full"
              [class.ng-invalid]="nameControl.invalid && nameControl.touched"
              [class.ng-dirty]="nameControl.touched"
            />
            <label for="name">{{ 'projectEdit.tokenAllocation.name' | translate }}</label>
          </p-floatlabel>
          @if (nameControl.invalid && nameControl.touched) {
            <small class="text-red-500 -mt-4">
              {{ getErrorMessage(nameControl, 'projectEdit.tokenAllocation.name') }}
            </small>
          }

          <!-- Percentage Input -->
          <p-floatlabel>
            <p-inputNumber
              inputId="percentage"
              [formControl]="percentageControl"
              [min]="0"
              [max]="100"
              [step]="1"
              suffix="%"
              [showButtons]="true"
              [class.ng-invalid]="percentageControl.invalid && percentageControl.touched"
              [class.ng-dirty]="percentageControl.touched"
              class="w-full"
            />
            <label for="percentage">{{ 'projectEdit.tokenAllocation.percentage' | translate }}</label>
          </p-floatlabel>
          @if (percentageControl.invalid && percentageControl.touched) {
            <small class="text-red-500 -mt-4">
              {{ getErrorMessage(percentageControl, 'projectEdit.tokenAllocation.percentage') }}
            </small>
          }
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-surface-200 dark:border-surface-700">
          <p-button
            [label]="'projectEdit.cancel' | translate"
            severity="secondary"
            [outlined]="true"
            (onClick)="onCancel()"
            type="button"
          />
          <p-button
            [label]="(isEditMode() ? 'projectEdit.tokenAllocation.updateAllocation' : 'projectEdit.tokenAllocation.addAllocation') | translate"
            [disabled]="allocationForm.invalid"
            type="submit"
          />
        </div>
      </form>
    </div>
  `,
  styles: [`
    :host ::ng-deep {
      .p-inputnumber {
        width: 100%;
      }
      
      .p-inputnumber-input {
        width: 100%;
      }
    }
  `]
})
export class AllocationDialogComponent implements OnInit {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);

  protected readonly isEditMode = signal(false);
  
  protected readonly allocationForm = new FormGroup({
    color: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(2)] }),
    percentage: new FormControl(0, { nonNullable: true, validators: [Validators.required, Validators.min(0), Validators.max(100)] })
  });

  protected get colorControl(): FormControl {
    return this.allocationForm.get('color') as FormControl;
  }

  protected get nameControl(): FormControl {
    return this.allocationForm.get('name') as FormControl;
  }

  protected get percentageControl(): FormControl {
    return this.allocationForm.get('percentage') as FormControl;
  }

  ngOnInit(): void {
    const allocation = this.config.data?.allocation as TokenAllocation | undefined;
    
    if (allocation) {
      this.isEditMode.set(true);
      this.allocationForm.patchValue({
        color: allocation.color,
        name: allocation.name,
        percentage: allocation.percentage
      });
    } else {
      // Set default color for new allocation
      const defaultColors = ['#806EDD', '#5C4AB8', '#FFB800', '#00D4FF', '#FF6B6B', '#4ECDC4', '#95E1D3'];
      const colorIndex = this.config.data?.colorIndex || 0;
      this.colorControl.setValue(defaultColors[colorIndex % defaultColors.length]);
    }
  }

  protected onSubmit(): void {
    if (this.allocationForm.valid) {
      const allocation: TokenAllocation = {
        color: this.colorControl.value,
        name: this.nameControl.value,
        percentage: this.percentageControl.value
      };
      this.dialogRef.close(allocation);
    }
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  protected getErrorMessage(control: FormControl, fieldName: string): string {
    if (!control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return `${fieldName} is required`;
    }
    if (control.errors['minlength']) {
      return `${fieldName} must be at least ${control.errors['minlength'].requiredLength} characters`;
    }
    if (control.errors['min']) {
      return `${fieldName} must be at least ${control.errors['min'].min}`;
    }
    if (control.errors['max']) {
      return `${fieldName} cannot exceed ${control.errors['max'].max}`;
    }

    return 'Invalid field';
  }
}
